/**
 * Payer Autocomplete Component
 *
 * Fuzzy search autocomplete for insurance payer selection
 */

"use client";

import {
  Command,
  CommandEmpty,
  CommandItem,
} from "@frontend/shared/components/ui/command";
import { Input } from "@frontend/shared/components/ui/input";
import { cn } from "@frontend/shared/lib/utils";
import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface PayerOption {
  id: number;
  payerName: string;
}

interface PayerAutocompleteProps {
  value: string;
  onChange: (value: string, payerId: string) => void;
  /** Override the default input className. */
  className?: string;
  /** Disable the auto-focus on mount (default: true). */
  autoFocus?: boolean;
  dropdownZIndex?: number;
}

export function PayerAutocomplete({
  value,
  onChange,
  className,
  autoFocus = true,
  dropdownZIndex,
}: PayerAutocompleteProps) {
  const t = useTranslations("InsuranceConnector");
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch payers from API
  const { data: payerData = [] } = useQuery<PayerOption[]>({
    queryKey: ["getPayerData"],
    queryFn: async () => {
      const response = await fetch("/api/insurance/getPayers");
      if (!response.ok) {
        return [];
      }
      return response.json();
    },
    initialData: [],
  });

  // Configure Fuse for fuzzy search
  const fuseOptions = {
    keys: ["payerName"],
    threshold: 0.3,
    distance: 50,
    minMatchCharLength: 1,
    shouldSort: true,
    includeScore: true,
  };

  const fuse = useMemo(() => new Fuse(payerData, fuseOptions), [payerData]);

  // Cache search results
  const searchCacheRef = useRef<{
    search: string;
    results: Array<{ item: PayerOption; score?: number }>;
  } | null>(null);

  // Invalidate cache when payer data changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: payerData changes trigger cache invalidation via fuse
  useEffect(() => {
    searchCacheRef.current = null;
  }, [payerData]);

  // Filter function for fuzzy search
  const fuseFilter = useCallback(
    (payerName: string, search: string) => {
      if (!search) return 1;

      const searchLower = search.toLowerCase();

      // Exact prefix match gets highest score
      if (payerName.toLowerCase().startsWith(searchLower)) {
        return 1;
      }

      // Use cached fuzzy search results
      let results =
        searchCacheRef.current?.search === search
          ? searchCacheRef.current.results
          : null;

      if (!results) {
        results = fuse.search(search);
        searchCacheRef.current = { search, results };
      }

      const match = results.find(
        (result) =>
          result.item.payerName.toLowerCase() === payerName.toLowerCase(),
      );

      return match?.score ? 1 - match.score : 0;
    },
    [fuse],
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  }, []);

  const handleSelect = useCallback(
    (payer: PayerOption) => {
      onChange(payer.payerName, payer.id.toString());
      setSearchValue(payer.payerName);
      setIsOpen(false);
    },
    [onChange],
  );

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Auto-select on exact match (case-insensitive)
  useEffect(() => {
    if (!searchValue || !payerData.length) return;

    const exactMatch = payerData.find(
      (payer) => payer.payerName.toLowerCase() === searchValue.toLowerCase(),
    );

    if (exactMatch) {
      onChange(exactMatch.payerName, exactMatch.id.toString());
    } else {
      // Clear selection if no exact match
      if (value) {
        onChange("", "");
      }
    }
  }, [searchValue, payerData, onChange, value]);

  return (
    <div className="relative w-full">
      <Command
        filter={fuseFilter}
        className="rounded-none bg-transparent"
        shouldFilter={isOpen}
      >
        <Input
          ref={inputRef}
          className={cn(
            "rounded-full py-[31px] text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0",
            className,
          )}
          placeholder={t("PayerPlaceholder")}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            if (value) {
              onChange("", "");
            }
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
        />
        {isOpen && (
          <div
            className="absolute left-0 right-0 top-[calc(100%+0.5rem)] bg-white rounded-3xl overflow-y-auto max-h-[200px] shadow-lg border border-gray-200"
            style={{ zIndex: dropdownZIndex ?? 50 }}
          >
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
              {t("NoPayersFound")}
            </CommandEmpty>
            {payerData
              .filter((payer) => fuseFilter(payer.payerName, searchValue) > 0)
              .sort((a, b) => {
                const scoreA = fuseFilter(a.payerName, searchValue);
                const scoreB = fuseFilter(b.payerName, searchValue);
                return scoreB - scoreA;
              })
              .slice(0, 50)
              .map((payer) => (
                <CommandItem
                  key={payer.id}
                  value={payer.payerName}
                  onSelect={() => handleSelect(payer)}
                  className="px-6 py-3 cursor-pointer hover:bg-gray-100 focus:bg-gray-100"
                >
                  {payer.payerName}
                </CommandItem>
              ))}
          </div>
        )}
      </Command>
    </div>
  );
}
